import store from "../store";
import { showToast } from "../store/actions";
import FetchApi from "../util/fetchApi";
import UpdateStore from "./updateStore";
const fetchApi = new FetchApi();

export default class Workspace extends UpdateStore {
  async getData() {
    return (await fetchApi.get(`/workspace`)).data;
  }

  async getAllWorkspaces() {
    return (await fetchApi.get(`/workspace/list`)).data;
  }

  async getInfo(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/info`)).data;
  }

  async restore(Workspace_id) {
    return (await fetchApi.get(`/workspace/restore/${Workspace_id}`)).data;
  }

  async getSchemaListByUser() {
    return (await fetchApi.get(`/workspace/schemasByUser`)).data;
  }

  async getWorkspaceDetail(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}`)).data;
  }

  async getUserList() {
    return (await fetchApi.get(`/workspace/getUserList`)).data.data;
  }

  async createWorkspace(postData) {
    return (await fetchApi.post(`/workspace`, postData)).data;
  }

  async getUserListToShare(Workspace_id) {
    return (await fetchApi.get(`/workspace/shareList/${Workspace_id}`)).data.data;
  }

  async getWorkspaceParticipants(Workspace_id, rolesToInclude) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/participants?rolesToInclude=${rolesToInclude.join(",")}`))
      .data;
  }

  async deleteWorkspace(Workspace_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}`)).data;
  }

  async shareWorkspace(Workspace_id, postData) {
    return (await fetchApi.post(`/workspace/${Workspace_id}`, postData)).data;
  }

  async getWorkspaceSettings(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/settings`)).data.data;
  }

  async updateWorkspaceSettings(Workspace_id, postData) {
    return (await fetchApi.patch(`/workspace/${Workspace_id}/settings`, postData)).data;
  }

  async getAdminAccess(Workspace_id, postData) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/adminAccess`, postData)).data;
  }

  async removeRoleToGetAdminAccess(Workspace_id, postData) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/removeRole`, postData)).data;
  }

  async removeUser(Workspace_id, postData) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/removeUser`, postData)).data;
  }

  async getLocationList(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/location`)).data.data;
  }

  async addLocationData(Workspace_id, postData, dataProvider) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/location/${dataProvider}`, postData)).data;
  }

  async updateLocationData(Workspace_id, Stage_id, postData, dataProvider) {
    return (await fetchApi.patch(`/workspace/${Workspace_id}/location/${dataProvider}/${Stage_id}`, postData)).data;
  }

  async deleteLocation(Workspace_id, Stage_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/location/${Stage_id}`)).data;
  }

  async getS3RegionLocation(Workspace_id, postData) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/location/s3-region`, postData)).data;
  }

  async loadSnowflakeFields(Workspace_id, data) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/quickimport/snowflake`, data)).data;
  }

  async loadLocalFile(Workspace_id, formData, token, customHeaders, customOptions) {
    return (
      await fetchApi.postFormData(
        `/workspace/${Workspace_id}/quickimport/upload?token=${token}`,
        formData,
        customHeaders,
        customOptions
      )
    ).data;
  }

  async loadDataConnectionData(Workspace_id, postData) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/quickimport/loadfromDataConnection`, postData)).data;
  }

  async getUploadHistory(Workspace_id, history_ids) {
    //Not showing error on UI
    //for handling random 502 error
    return (
      await fetchApi.get(
        `/workspace/${Workspace_id}/quickimport/getHistory?history_ids=${history_ids}`,
        (data) => data,
        () => ({ onErrorReturn: true, errorResponse: null })
      )
    )?.data;
  }

  async getDatabaseList(Workspace_id, Stage_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/snowflake/${Stage_id}`)).data;
  }

  async getExternalSnowflakeCluster(Workspace_id, Stage_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/snowflake/${Stage_id}/clusterList`)).data;
  }

  async getExternalSnowflakeSchemas(Workspace_id, Stage_id, postData) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/snowflake/${Stage_id}/schemaList`, postData)).data;
  }

  async getExternalSnowflakeTables(Workspace_id, Stage_id, postData) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/snowflake/${Stage_id}/tableList`, postData)).data;
  }

  async runSnowflakeQuery(Workspace_id, Stage_id, postData) {
    return (
      await fetchApi.post(
        `/workspace/${Workspace_id}/snowflake/${Stage_id}/runquery`,
        postData,
        (data) => data,
        (error) => {
          if (error.response.status === 504) {
            store.dispatch(
              showToast({
                message: "Timeout due to long query. The query took too long to execute and could not be previewed.",
                type: "error",
              })
            );

            return { onErrorReturn: true, errorResponse: error.response };
          }
          return {};
        }
      )
    ).data;
  }

  async getConnectionData(Workspace_id, Stage_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/location/${Stage_id}`)).data.data;
  }

  async getAllJobs(Workspace_id, params = {}) {
    const { search = "", orderBy = "", order = "", jobTypeList = [], jobIds = [], pipelineList = [] } = params;

    const queryParameters = [
      search && `search=${encodeURIComponent(search)}`,
      orderBy && `orderBy=${orderBy}`,
      order && `order=${order}`,
      jobTypeList.length && `jobTypeList=${jobTypeList.join(",")}`,
      pipelineList.length && `pipelineList=${pipelineList.join(",")}`,
      jobIds.length && `jobIds=${jobIds.join(",")}`,
    ]
      .filter(Boolean)
      .join("&");

    const queryString = queryParameters ? `?${queryParameters}` : "";
    return (await fetchApi.get(`/workspace/${Workspace_id}/job/list${queryString}`)).data;
  }

  // Getting all schemas and only schemas on which user have read permission
  async getJobsSchemas(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/job/schemas`)).data;
  }

  async dataloadJob(param, Workspace_id, Job_id, jobType, validate) {
    let path = "dataload";
    if (jobType === 4) path = "dataQuality";
    if (jobType === 5) path = "mdm";
    if (jobType === 6) path = "deduplication";
    return (
      await fetchApi.get(`/workspace/${Workspace_id}/job/${path}/${Job_id}/` + param + `?validate=${validate ?? false}`)
    ).data;
  }

  async saveJobData(postData, Workspace_id, Job_id) {
    return (await fetchApi.patch(`/workspace/${Workspace_id}/job/${Job_id}`, postData)).data;
  }

  async getTimezone(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/job/timezone`)).data;
  }

  async getDataModelingJobSchedule(Workspace_id, Job_id, JobType) {
    const JobTypeURL = { 1: "dataLoad", 4: "dataQuality", 5: "mdm", 6: "deduplication" };
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/${JobTypeURL[JobType]}/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return { data: "Unauthorized", success: false };
    return response.data;
  }

  async getDataLoadSchedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/dataLoad/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async getMDMSchedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/mdm/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async getDeduplicationschedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/deduplication/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async createDataModelingJob(postData, Workspace_id, JobType) {
    const JobTypeURL = { 1: "dataLoad", 4: "dataQuality", 5: "mdm", 6: "deduplication" };
    return (await fetchApi.post(`/workspace/${Workspace_id}/job/${JobTypeURL[JobType]}`, postData)).data;
  }

  async updateDataModelingJob(postData, Workspace_id, Job_id, JobType) {
    const JobTypeURL = { 1: "dataLoad", 4: "dataQuality", 5: "mdm", 6: "deduplication" };

    return (await fetchApi.patch(`/workspace/${Workspace_id}/job/${JobTypeURL[JobType]}/${Job_id}`, postData)).data;
  }

  async deleteDataLoadJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/dataLoad/${Job_id}`)).data;
  }

  async deleteMDMJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/mdm/${Job_id}`)).data;
  }

  async deleteDeduplicationJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/deduplication/${Job_id}`)).data;
  }

  async getDataQualitySchedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/dataQuality/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async deleteDataQualityJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/dataQuality/${Job_id}`)).data;
  }

  async getDataExchangeSchedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/dataExchange/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async getNotebookSchedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/notebook/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async getJobStatus(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/refresh/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async runDataExchangeJob(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/dataExchange/run/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async createExchangeJob(postData, Workspace_id, type) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/job/dataExchange`, postData)).data;
  }

  async updateExchangeJob(postData, Workspace_id, Job_id) {
    return (await fetchApi.patch(`/workspace/${Workspace_id}/job/dataExchange/${Job_id}`, postData)).data;
  }

  async deleteExchangeJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/dataExchange/${Job_id}`)).data;
  }

  async getDataTransformSchedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/dataTransform/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async runDataTranformJob(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/dataTransform/run/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async getRoutingSchedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/routing/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async runRoutingJob(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/routing/run/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async stopJobExecutionById(Workspace_id, Job_id, JobType) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/job/${Job_id}/stop`, { JobType })).data;
  }

  async createTransformJob(postData, Workspace_id, type) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/job/dataTransform`, postData)).data;
  }

  async updateTransformJob(postData, Workspace_id, Job_id) {
    return (await fetchApi.patch(`/workspace/${Workspace_id}/job/dataTransform/${Job_id}`, postData)).data;
  }

  async deleteTransformJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/dataTransform/${Job_id}`)).data;
  }

  async createRoutingJob(postData, Workspace_id, type) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/job/routing`, postData)).data;
  }

  async updateRoutingJob(postData, Workspace_id, Job_id) {
    return (await fetchApi.patch(`/workspace/${Workspace_id}/job/routing/${Job_id}`, postData)).data;
  }

  async deleteRoutingJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/routing/${Job_id}`)).data;
  }

  async runNotebookJob(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/notebook/run/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async createNotebookJob(postData, Workspace_id, type) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/job/notebook`, postData)).data;
  }

  async updateNotebookJob(postData, Workspace_id, Job_id) {
    return (await fetchApi.patch(`/workspace/${Workspace_id}/job/notebook/${Job_id}`, postData)).data;
  }

  async deleteNotebookJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/notebook/${Job_id}`)).data;
  }

  async getNotificationSchedule(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/notification/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async createNotificationJob(postData, Workspace_id) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/job/notification`, postData)).data;
  }

  async updateNotificationJob(postData, Workspace_id, Job_id) {
    return (await fetchApi.patch(`/workspace/${Workspace_id}/job/notification/${Job_id}`, postData)).data;
  }

  async deleteNotificationJob(Workspace_id, Job_id) {
    return (await fetchApi.delete(`/workspace/${Workspace_id}/job/notification/${Job_id}`)).data;
  }

  async runNotificationJob(Workspace_id, Job_id) {
    const response = await fetchApi.get(`/workspace/${Workspace_id}/job/notification/run/${Job_id}`);
    if (
      response.status === 401 ||
      response.statusText === "Unauthorized" ||
      (response.data.errorType && response.data.errorType === "credentials")
    )
      return "Unauthorized";
    return response.data;
  }

  async getJobHistory(Workspace_id, Job_id, startDate, endDate, { orderBy, order }, UserTimezone) {
    startDate = startDate ? `startdate=${startDate}&` : "";
    endDate = endDate ? `enddate=${endDate}&` : "";
    let paramQuery = `orderBy=${orderBy}&order=${order}&tz=${UserTimezone}`;
    return (await fetchApi.get(`/workspace/${Workspace_id}/job/${Job_id}/history?${startDate}${endDate}${paramQuery}`))
      .data;
  }

  async getDetailedJobHistory(
    Workspace_id,
    Job_id,
    startDate,
    endDate,
    { search, limit, page, orderBy, order, UserTimezone },
    JobHistory_id
  ) {
    startDate = startDate ? `startdate=${startDate}&` : "";
    endDate = endDate ? `enddate=${endDate}&` : "";
    let paramQuery = `limit=${limit}&page=${page}&orderBy=${orderBy}&order=${order}&search=${search}&tz=${UserTimezone}`;

    return (
      await fetchApi.get(
        `/workspace/${Workspace_id}/job/${Job_id}/history/${JobHistory_id}?${startDate}${endDate}${paramQuery}`
      )
    ).data;
  }

  async getErroredRecordsList(Workspace_id, Job_id, JobHistory_id, SourceID, File) {
    return (
      await fetchApi.post(`/workspace/${Workspace_id}/job/${Job_id}/history/${JobHistory_id}/errorRecords/`, {
        SourceID,
        File,
      })
    ).data;
  }

  async getUserRoles(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/userRoles`)).data;
  }

  async getChainJobs(Workspace_id, Job_id, DataPipeline_id) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/job/chain`, { Job_id, DataPipeline_id })).data;
  }

  async getImportWorkspaceDetails(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/importconfiglist`)).data;
  }

  async compareWorkspaces(Workspace_id, type, submitData) {
    if (type === 0) return (await fetchApi.post(`/workspace/${Workspace_id}/compareType/${type}`, submitData)).data;
    else if (type === 1)
      return (await fetchApi.postFormData(`/workspace/${Workspace_id}/compareType/${type}`, submitData)).data;
  }

  async getWorkspaceConfiguration(Workspace_id) {
    return (await fetchApi.get(`/workspace/${Workspace_id}/getConfiguration`)).data;
  }

  async importWorkspaceFrom(Workspace_id, type, postData) {
    if (type === 0)
      return (
        await fetchApi.post(`/workspace/${Workspace_id}/importfromType/${type}`, postData, (response) =>
          this.updateOnImportSuccess(response)
        )
      ).data;

    if (type === 1)
      return (
        await fetchApi.postFormData(
          `/workspace/${Workspace_id}/importfromType/${type}`,
          postData,
          undefined,
          undefined,
          (response) => this.updateOnImportSuccess(response)
        )
      ).data;
  }

  async exportWorkspace(Workspace_id, fileName, postData, onSuccess) {
    return await fetchApi.downloadPost(
      `/workspace/${Workspace_id}/exportWorkspace`,
      postData,
      `${fileName}.json`,
      onSuccess
    );
  }

  async loadFileColumns(Workspace_id, data) {
    return (await fetchApi.post(`/workspace/${Workspace_id}/utils/previewFile`, data)).data;
  }
}
